import React from 'react';

import DataConnector from 'connect/DataConnector';
import {ListedLocation} from 'constants/SampleConstants';
import Breadcrumbs, {CrumbLink, getCrumbsFromListed, ListedCrumbItem} from 'nav/Breadcrumbs';
import {getNearestAncestralLocationId} from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import {ParentStorableDetailsInfo} from 'samples/storables/ParentStorableDetails';
import StorableStore from 'samples/StorableStore';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import APIUtil from 'util/APIUtil';

class ParentStorableBreadcrumbsConnector extends DataConnector<
  ParentStorableBreadcrumbsProps,
  EnhancedProps
> {
  override stores = [StorableStore];

  enhanceProps(): EnhancedProps {
    const listedLocationId = getNearestAncestralLocationId(this.props.parentStorable);
    if (listedLocationId) {
      return {listedLocation: this.storableStore().getListedLocation(listedLocationId)};
    } else {
      return {listedLocation: null};
    }
  }
}

interface ParentStorableBreadcrumbsProps {
  parentStorable?: ParentStorableDetailsInfo | null;
}

interface EnhancedProps {
  listedLocation: ListedLocation | null;
}

class ParentStorableBreadcrumbs extends React.Component<ParentStorableBreadcrumbsProps & EnhancedProps> {
  private refresh(): void {
    const listedLocationId = getNearestAncestralLocationId(this.props.parentStorable);
    if (listedLocationId) {
      StorableActions.fetchLocation(listedLocationId);
    }
  }

  override componentDidMount(): void {
    this.refresh();
  }

  override componentDidUpdate(prevProps: ParentStorableBreadcrumbsProps & EnhancedProps): void {
    if (this.props.parentStorable?.id !== prevProps.parentStorable?.id) {
      this.refresh();
    }
  }

  override render(): JSX.Element {
    const parentStorable = this.props.parentStorable;
    if (!parentStorable) {
      return (
        <div className="u-faded" {...getTestProps({component: 'ParentStorableBreadcrumbs', element: 'root'})}>
          None
        </div>
      );
    }

    // Only show links for parent Locations for signed-in users, since signed-out users viewing
    // public items should not have access to Location listings
    const crumbs = EntityStore.currentUser()
      ? getCrumbsFromListed({
          listed: this.props.listedLocation,
          openResult: StorableActions.open,
          shouldLinkLastCrumb: true,
          renderIcon: (item?: ListedCrumbItem) => undefined,
        })
      : [];
    if (!APIUtil.isLocation(parentStorable.id)) {
      crumbs.push(
        <CrumbLink
          key={`crumb-${parentStorable.id}`}
          onClick={() => {
            StorableActions.open(parentStorable);
          }}
          text={parentStorable.name}
        />
      );
    }
    return (
      <Breadcrumbs
        crumbs={crumbs}
        dataTestAttrs={{component: 'ParentStorableBreadcrumbs', element: 'root'}}
      />
    );
  }
}

export default ParentStorableBreadcrumbsConnector.connect(ParentStorableBreadcrumbs);
